import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Music2, Sparkles, Loader2 } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import api, { API } from "@/lib/api";
import { MY_CREATIONS } from "@/constants/testIds";
import SEO from "@/components/seo/SEO";

export default function MyCreations() {
  const { t } = useI18n();
  const [trials, setTrials] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const loadTrials = useCallback(async () => {
    const { data } = await api.get("/guest/my-trials");
    setTrials(data);
  }, []);

  useEffect(() => {
    const pending = localStorage.getItem("ams_pending_trial_claim");
    if (pending) {
      setClaiming(true);
      const { generation_id, guest_session_id } = JSON.parse(pending);
      api
        .post("/guest/claim", { generation_id, guest_session_id })
        .catch(() => null)
        .finally(() => {
          localStorage.removeItem("ams_pending_trial_claim");
          setClaiming(false);
          loadTrials();
        });
    } else {
      loadTrials();
    }
  }, [loadTrials]);

  const token = localStorage.getItem("ams_token");
  const fullUrl = (id) => `${API}/guest/generation/${id}/full?auth=${token}`;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
      <SEO title="Mes créations | Kalypso Music" description="Retrouvez et écoutez toutes vos chansons personnalisées créées avec Kalypso Music." path="/my-creations" noindex />
        <h1 className="font-heading text-2xl font-medium">{t("myCreations.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("myCreations.subtitle")}</p>

        {claiming && (
          <div data-testid={MY_CREATIONS.claimingBanner} className="mt-6 flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm text-primary">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("myCreations.claiming")}
          </div>
        )}

        <div data-testid={MY_CREATIONS.container} className="mt-8 space-y-4">
          {trials === null ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : trials.length === 0 ? (
            <div data-testid={MY_CREATIONS.empty} className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">
              <Music2 className="mx-auto mb-3 h-8 w-8 opacity-40" />
              {t("myCreations.empty")}
              <div className="mt-4">
                <Link to="/">
                  <Button variant="outline" className="rounded-full">
                    {t("myCreations.goHome")}
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            trials.map((tr) => (
              <div key={tr.generation_id} data-testid={MY_CREATIONS.item(tr.generation_id)} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <Music2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold">{tr.title}</p>
                    <p className="text-xs capitalize text-muted-foreground">
                      {t(`options.style_${tr.style}`)} · {t(`options.mood_${tr.mood}`)}
                    </p>
                  </div>
                </div>
                <audio
                  data-testid={MY_CREATIONS.player(tr.generation_id)}
                  controls
                  controlsList="nodownload noplaybackrate"
                  className="mt-4 w-full"
                  src={fullUrl(tr.generation_id)}
                />
                <div className="mt-4 rounded-xl bg-muted/50 p-4">
                  <p className="text-sm font-medium">{t("myCreations.unlockCta")}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{t("myCreations.unlockDesc")}</p>
                  <Link to="/create">
                    <Button data-testid={MY_CREATIONS.unlockCta(tr.generation_id)} size="sm" className="mt-3 rounded-full">
                      <Sparkles className="mr-1.5 h-3.5 w-3.5" /> {t("myCreations.unlockCta")}
                    </Button>
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
